import { patchCoreField } from '@api/entity-attributes';
import { listPortalSiteVendors, type PortalSiteVendor } from '@api/vendors';
import NotificationCard from '@components/containers/settings/NotificationCard';
import OrderEmailSection from '@components/containers/settings/OrderEmailSection';
import { Badge, Group, Stack, Text, TextInput } from '@mantine/core';
import useApp from '@providers/App/useApp';
import { useQuery } from '@tanstack/react-query';
import { useState, type FC } from 'react';

/**
 * One manufacturer's contact address, editable in place.
 *
 * EDITABLE HERE BECAUSE THIS IS WHERE THE QUESTION IS ASKED. The list exists so a brand can see
 * who gets copied without leaving the screen; sending them to another section to FIX a wrong
 * address undoes half of that. It writes through `patchCoreField`, the same endpoint the
 * Vendors grid uses for its own inline edits, so the two cannot drift on validation.
 *
 * IT IS NOT "THE NOTIFICATION ADDRESS", and the caption under the list says so. `vendors.email`
 * is also read when a production order is issued and when a purchase order seeds its vendor
 * details, so an edit here moves where that manufacturer's next ORDER goes, not merely who is
 * copied on an acknowledgement. Documents already issued snapshot the address and do not move.
 *
 * ADMIN ONLY, matching the recipient lists above. The patch endpoint itself is authenticated
 * but not admin-gated, so without this a non-admin could edit a vendor's address here while
 * being unable to touch the To list two inches above it — which reads as a bug even though
 * each behaviour is defensible on its own.
 *
 * The secondary address is shown but not editable: it is copied on the email too, and it is not
 * a core column on the vendors grid, so there is no established write path for it.
 */
const ManufacturerAddressRow: FC<{
    vendor: PortalSiteVendor;
    editable: boolean;
    onSaved: () => void;
}> = ({ vendor, editable, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(vendor.email ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const commit = async () => {
        const value = draft.trim();
        if (value === (vendor.email ?? '')) {
            setEditing(false);
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await patchCoreField('VENDOR', vendor.vendorId, 'email', value);
            setEditing(false);
            onSaved();
        } catch (err: any) {
            setError(
                err?.error?.data?.message ?? err?.error?.message ?? 'Could not save that address.',
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack gap={2}>
            <Group gap="xs" wrap="nowrap" align="center">
                <Text size="xs" w={130} truncate>
                    {vendor.name}
                </Text>

                {editing ? (
                    <TextInput
                        size="xs"
                        w={280}
                        autoFocus
                        value={draft}
                        disabled={saving}
                        placeholder="name@company.com"
                        onChange={(e) => setDraft(e.currentTarget.value)}
                        // Blur commits, the way the entity grid's cells do. Escape abandons the
                        // draft, because a half-typed address saved by accident is worse than a
                        // lost keystroke.
                        onBlur={() => void commit()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') void commit();
                            if (e.key === 'Escape') {
                                setDraft(vendor.email ?? '');
                                setEditing(false);
                            }
                        }}
                    />
                ) : (
                    <Text
                        size="xs"
                        c={vendor.email ? undefined : 'orange.7'}
                        td={editable ? 'underline dotted' : undefined}
                        style={editable ? { cursor: 'pointer' } : undefined}
                        onClick={() => {
                            if (!editable) return;
                            setDraft(vendor.email ?? '');
                            setEditing(true);
                        }}
                    >
                        {vendor.email ?? 'No address on file — nobody would be copied'}
                    </Text>
                )}

                {vendor.secondaryEmail && (
                    <Text size="xs" c="dimmed">
                        + {vendor.secondaryEmail}
                    </Text>
                )}

                {/*
                  * A site with no login can never press Acknowledge, so its address will never
                  * actually be written to. Saying so beats listing it as though it were live.
                  */}
                {!vendor.hasLogin && (
                    <Badge size="xs" variant="light" color="gray">
                        no portal login yet
                    </Badge>
                )}
            </Group>

            {error && (
                <Text size="xs" c="red.7" ml={138}>
                    {error}
                </Text>
            )}
        </Stack>
    );
};

/**
 * Settings → Email Settings. Who gets told when something happens.
 *
 * ONE SECTION PER EMAIL, selected by the pills at the top, the same shape Distribution Email
 * uses to pick a channel. They are genuinely different configurations rather than one form with
 * a filter: an acknowledgement and a QC verdict are brand-wide announcements with one list
 * each, while an order email goes to one counterparty at a time and is keyed per vendor.
 *
 * ADMIN ONLY, matching Brand Letterhead. The server refuses anyone else; hiding the controls
 * keeps a consultant from filling in a form that will 403 on save.
 *
 * NOTHING SENDS UNTIL SOMEBODY IS NAMED. An empty To is the off switch regardless of the
 * toggle, which is what keeps all of this invisible to every brand that has not asked for it.
 */
const SECTIONS = [
    { key: 'acknowledge', label: 'Acknowledge' },
    { key: 'production-order', label: 'Production order' },
    { key: 'purchase-order', label: 'Purchase order' },
    { key: 'qc', label: 'QC' },
] as const;

type SectionKey = (typeof SECTIONS)[number]['key'];

const EmailSettingsTab: FC = () => {
    const { currentUser } = useApp();
    const isAdmin = currentUser?.role === 'ADMIN';
    const [section, setSection] = useState<SectionKey>('acknowledge');

    /*
     * WHO THE MANUFACTURER HALF OF THE ACKNOWLEDGEMENT REACHES. That card says the manufacturer
     * is copied at the address on its supplier record, and that sentence is useless on its own:
     * there is no single address, there is one per manufacturer, and a brand had to leave this
     * screen and look each one up. This is that lookup, done here.
     */
    const manufacturers = useQuery({
        queryKey: ['notification-portal-site-vendors'],
        queryFn: listPortalSiteVendors,
    });

    const sectionPills = (
        <Group gap="xs" wrap="wrap">
            {SECTIONS.map((entry) => (
                <Badge
                    key={entry.key}
                    size="lg"
                    variant={section === entry.key ? 'filled' : 'outline'}
                    color={section === entry.key ? 'blue' : 'gray'}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSection(entry.key)}
                >
                    {entry.label}
                </Badge>
            ))}
        </Group>
    );

    const manufacturerAddresses = (manufacturers.data?.length ?? 0) > 0 && (
        <Stack gap={4} mt="xs">
            <Text size="xs" fw={600} c="dimmed">
                Manufacturers are copied at these addresses
            </Text>
            {manufacturers.data?.map((vendor) => (
                <ManufacturerAddressRow
                    key={vendor.vendorId}
                    vendor={vendor}
                    editable={isAdmin}
                    onSaved={() => void manufacturers.refetch()}
                />
            ))}
            <Text size="xs" c="dimmed">
                This is the vendor&apos;s contact address, so changing it also changes where that
                manufacturer&apos;s production and purchase orders are sent. Orders already
                issued keep the address they went out to.
            </Text>
        </Stack>
    );

    return (
        <Stack gap="md">
            {sectionPills}

            {section === 'acknowledge' && (
                <NotificationCard
                    event="order-acknowledged"
                    title="Production order acknowledged"
                    description="Sent when a manufacturer presses Acknowledge on an order in its portal. Whichever manufacturer acknowledged is copied automatically, so none of them need adding here."
                    ccDescription="Anyone else who should see it, beyond the manufacturer."
                    footer={manufacturerAddresses}
                    editable={isAdmin}
                />
            )}

            {(section === 'production-order' || section === 'purchase-order') && (
                <OrderEmailSection document={section} editable={isAdmin} />
            )}

            {/*
              * THREE LISTS, for three events. The receipt is the common one: every delivery,
              * with the verdict the site gave at the gate. A correction is the rare one: a
              * batch's verdict changed afterwards. Finished goods are the brand's own call and
              * usually a different desk. A brand where it is all the same people puts the same
              * addresses in each, which costs nothing.
              *
              * Which QC list a later verdict uses is decided by WHAT WAS JUDGED, not by who
              * pressed the button: the site's route is material-only, but the brand's is not
              * restricted and may rule on either.
              */}
            {section === 'qc' && (
                <>
                    <NotificationCard
                        event="receipt-qc"
                        title="Receipt QC"
                        description="Sent once per delivery a site records, with the verdict it gave at the gate — every line, what was approved, and what went back with the reason. The site that recorded it is copied automatically."
                        switchLabel="Send receipt QC emails"
                        editable={isAdmin}
                        perManufacturer
                    />
                    <NotificationCard
                        event="material-qc"
                        title="QC correction"
                        description="Sent when a material batch's verdict is changed after it was received — a correction, a hold, or a re-ruling from the stock screen. The gate verdict itself is the Receipt QC email above."
                        switchLabel="Send QC correction emails"
                        editable={isAdmin}
                    />
                    <NotificationCard
                        event="product-qc"
                        title="Finished goods QC"
                        description="Sent when finished goods a site produced are ruled on. Quality on finished goods is the brand's call, not the site's, so this is usually a different set of people."
                        switchLabel="Send finished goods QC emails"
                        editable={isAdmin}
                    />
                    <Text size="xs" c="dimmed">
                        Verdicts recorded by a sync rather than a person are never emailed — a
                        verdict nobody made is not news to anybody.
                    </Text>
                </>
            )}
        </Stack>
    );
};

export default EmailSettingsTab;
